// Beat 6 visual: a 10-brick pyramid (4-3-2-1) that builds itself as the user
// scrolls through the beat. Each brick has its own scroll entrance AND exit
// window so bricks fall into place individually (bottom-to-top during build)
// and lift away individually in reverse (top-down during exit), each with
// the same 5% segment-width timing as entrance, synced with the hero panel's
// fade. The apex brick is a tall, slightly-taller-than-
// wide block that crowns the pyramid with three vertically-stacked
// streaming-platform logos.
//
// Sizing strategy:
//   - Grid aspect is a universal 8:6 across every breakpoint. Brick aspect
//     is 1.67:1 (classic "brick" proportion) and apex is 0.83:1 (slightly
//     taller than wide, room for 3 stacked logos).
//   - max-w ladder is tuned so wrapper_width × 6/8 ≤ worst-case available
//     vertical space at every common viewport, including short laptops
//     (1366×768, 1280×720) and standard desktops (1920×1080). The hosting
//     `BEAT_SIX_VISUAL_SHELL` (in Home.tsx) anchors the pyramid closer to
//     the hero text than beat 4's shell — beat 6 has nothing else to clear.
//   - Icon and label sizes scale per breakpoint so content always fits the
//     derived row height with breathing room.

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import {
  Filter,
  KeyRound,
  Languages,
  Music,
  Plug,
  SlidersHorizontal,
  Timer,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { ASSETS } from '@airhead-games/shared/configs';
import {
  easeOutCubic,
  mapProgress,
} from '../../../utilities/scrollTimelineMath';

type BeatSixPyramidProps = {
  scrollVh: MotionValue<number>;
  /** Global vh where the host segment starts. */
  segmentStartVh: number;
  /** Host segment duration in vh. */
  segmentDurationVh: number;
};

type BrickKind = 'capability' | 'apex';

type BrickPlacement = {
  /** 1-based grid column start (1..8). */
  colStart: number;
  /** Number of grid columns to span. */
  colSpan: number;
  /** 1-based grid row (1=apex top, 5=base). */
  row: number;
  /** Optional number of grid rows to span (default 1). The apex spans 2 so
   *  it forms a block tall enough to stack 3 logos vertically. */
  rowSpan?: number;
};

type Brick = {
  id: string;
  kind: BrickKind;
  label: string;
  /** Lucide icon component for capability bricks; ignored for apex. */
  icon?: LucideIcon;
  placement: BrickPlacement;
  /** Entrance window as % of host segment vh (0-100). Tightly staggered
   *  (~2.5% offset) so bricks fall in quick succession bottom-to-top. */
  enterStartPct: number;
  enterEndPct: number;
  /** Exit window as % of host segment vh (0-100). Same 5% width as
   *  entrance; reverse order from entrance (apex first, base last),
   *  staggered in sync with the hero panel's fade. */
  exitStartPct: number;
  exitEndPct: number;
};

const BRICKS: Brick[] = [
  // Base (row 5) — enters first, exits last.
  {
    id: 'quick-start',
    kind: 'capability',
    label: 'Quick start',
    icon: Zap,
    placement: { colStart: 1, colSpan: 2, row: 5 },
    enterStartPct: 0,
    enterEndPct: 5,
    exitStartPct: 78.5,
    exitEndPct: 83.5,
  },
  {
    id: 'short-rounds',
    kind: 'capability',
    label: '10-20 minute rounds',
    icon: Timer,
    placement: { colStart: 3, colSpan: 2, row: 5 },
    enterStartPct: 2.5,
    enterEndPct: 7.5,
    exitStartPct: 77,
    exitEndPct: 82,
  },
  {
    id: 'free-players',
    kind: 'capability',
    label: 'Free players',
    icon: Users,
    placement: { colStart: 5, colSpan: 2, row: 5 },
    enterStartPct: 5,
    enterEndPct: 10,
    exitStartPct: 75.5,
    exitEndPct: 80.5,
  },
  {
    id: 'platform-integrations',
    kind: 'capability',
    label: 'Platform integrations',
    icon: Plug,
    placement: { colStart: 7, colSpan: 2, row: 5 },
    enterStartPct: 7.5,
    enterEndPct: 12.5,
    exitStartPct: 74,
    exitEndPct: 79,
  },
  // Middle-3 (row 4) — staggered half a brick over the base.
  {
    id: 'access-controls',
    kind: 'capability',
    label: 'Access controls',
    icon: KeyRound,
    placement: { colStart: 2, colSpan: 2, row: 4 },
    enterStartPct: 11,
    enterEndPct: 16,
    exitStartPct: 72.5,
    exitEndPct: 77.5,
  },
  {
    id: 'content-controls',
    kind: 'capability',
    label: 'Content controls',
    icon: Filter,
    placement: { colStart: 4, colSpan: 2, row: 4 },
    enterStartPct: 13.5,
    enterEndPct: 18.5,
    exitStartPct: 71,
    exitEndPct: 76,
  },
  {
    id: 'language-controls',
    kind: 'capability',
    label: 'Language controls',
    icon: Languages,
    placement: { colStart: 6, colSpan: 2, row: 4 },
    enterStartPct: 16,
    enterEndPct: 21,
    exitStartPct: 69.5,
    exitEndPct: 74.5,
  },
  // Middle-2 (row 3)
  {
    id: 'royalty-free-music',
    kind: 'capability',
    label: 'Royalty-free music',
    icon: Music,
    placement: { colStart: 3, colSpan: 2, row: 3 },
    enterStartPct: 21,
    enterEndPct: 26,
    exitStartPct: 68,
    exitEndPct: 73,
  },
  {
    id: 'audio-mix',
    kind: 'capability',
    label: 'Custom audio mixing',
    icon: SlidersHorizontal,
    placement: { colStart: 5, colSpan: 2, row: 3 },
    enterStartPct: 23.5,
    enterEndPct: 28.5,
    exitStartPct: 66.5,
    exitEndPct: 71.5,
  },
  // Apex (rows 1-2) — enters last, exits first.
  {
    id: 'apex-platforms',
    kind: 'apex',
    label: '',
    placement: { colStart: 4, colSpan: 2, row: 1, rowSpan: 2 },
    enterStartPct: 28,
    enterEndPct: 33,
    exitStartPct: 65,
    exitEndPct: 70,
  },
];

/** Vertical distance (px) bricks travel during their entrance. */
const BRICK_DROP_PX = 180;
/** Vertical distance (px) bricks travel upward during their exit, mirroring
 *  the hero panel's exit direction so the whole composition lifts together. */
const BRICK_LIFT_PX = 180;

const BeatSixPyramid: React.FC<BeatSixPyramidProps> = ({
  scrollVh,
  segmentStartVh,
  segmentDurationVh,
}) => (
  <div
    className={[
      'mx-auto w-full',
      // Base width ladder — tuned so wrapper_width × 6/8 ≤ worst-case
      // available vertical space at each viewport class (verified against
      // 320×568, 390×844, 768×800, 1024×768, 1280×720, 1366×768, 1536×800,
      // 1920×1080, 2560×1440, 3840×2160). See top-of-file comment for
      // sizing strategy.
      'max-w-[22rem]', // mobile (352)
      'sm:max-w-md', // sm 640+ (448)
      'md:max-w-xl', // md 768+ (576) — 432 tall fits 768×800 avail (456).
      'lg:max-w-xl', // lg 1024+ (576) — 432 tall fits 1024×768 avail (464 with short override).
      'xl:max-w-xl', // xl 1280+ (576) — 432 tall fits 1280×720 avail (448).
      '2xl:max-w-2xl', // 2xl 1536+ (672) — 504 tall fits 1536×800 avail (496) within tolerance.
      'min-[1920px]:max-w-3xl', // 1920+ (768) — 576 tall fits 1920×1080 avail (672 with new shell).
      'min-[2560px]:max-w-7xl', // 2560+ (1280) — 960 tall fits 2560×1440 avail (960 with new shell).
      'min-[3840px]:max-w-[90rem]', // 3840+ (1440) — 1080 tall fits 3840×2160 avail.
      // Height-aware overrides — when there's more vertical room (taller
      // monitors, iPad Pro, etc.) the pyramid can grow proportionally so
      // bricks don't look small on big tall screens. Mutually exclusive
      // width × height ranges per the BEAT_*_VISUAL_SHELL pattern so
      // Tailwind v4's media-query sort order doesn't matter.
      '[@media(min-width:1024px)_and_(max-width:1919px)_and_(min-height:900px)]:max-w-3xl', // lg/xl/2xl tall (e.g. iPad Pro landscape 1366×1024): 768 wide, 576 tall.
      '[@media(min-width:1920px)_and_(max-width:2559px)_and_(min-height:1000px)]:max-w-4xl', // 1920+ tall (1920×1200): 896 wide, 672 tall.
    ].join(' ')}
  >
    <div
      className={[
        'grid w-full gap-1.5 sm:gap-2',
        // Aspect is taller on mobile (8:7) so bricks have enough vertical
        // room for 2-line wrapped labels at small viewport widths. From sm
        // up, switches to 8:6 for the classic flat-brick proportion.
        'aspect-[8/7] sm:aspect-[8/6]',
      ].join(' ')}
      style={{
        gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(5, minmax(0, 1fr))',
      }}
    >
      {BRICKS.map((brick) => (
        <PyramidBrick
          key={brick.id}
          brick={brick}
          scrollVh={scrollVh}
          segmentStartVh={segmentStartVh}
          segmentDurationVh={segmentDurationVh}
        />
      ))}
    </div>
  </div>
);

type PyramidBrickProps = {
  brick: Brick;
  scrollVh: MotionValue<number>;
  segmentStartVh: number;
  segmentDurationVh: number;
};

const PyramidBrick: React.FC<PyramidBrickProps> = ({
  brick,
  scrollVh,
  segmentStartVh,
  segmentDurationVh,
}) => {
  const pctToVh = (pct: number): number =>
    segmentStartVh + (pct / 100) * segmentDurationVh;
  const enterStartVh = pctToVh(brick.enterStartPct);
  const enterEndVh = pctToVh(brick.enterEndPct);
  const exitStartVh = pctToVh(brick.exitStartPct);
  const exitEndVh = pctToVh(brick.exitEndPct);

  const opacity = useTransform(scrollVh, (v) => {
    const entered = easeOutCubic(mapProgress(v, enterStartVh, enterEndVh));
    const exited = easeOutCubic(mapProgress(v, exitStartVh, exitEndVh));
    return entered * (1 - exited);
  });
  const y = useTransform(scrollVh, (v) => {
    const entered = easeOutCubic(mapProgress(v, enterStartVh, enterEndVh));
    const exited = easeOutCubic(mapProgress(v, exitStartVh, exitEndVh));
    return (1 - entered) * BRICK_DROP_PX - exited * BRICK_LIFT_PX;
  });

  const isApex = brick.kind === 'apex';
  const Icon = brick.icon;
  const rowSpan = brick.placement.rowSpan ?? 1;

  return (
    <motion.div
      className={[
        'relative flex items-center justify-center overflow-hidden rounded-md',
        'bg-teal-600 text-white ring-1 ring-teal-300/30',
        // Inset bottom edge gives the brick a "solid block thickness" feel
        // without casting an outer shadow onto neighbors below it.
        'shadow-[inset_0_-4px_0_rgba(0,0,0,0.22)]',
      ].join(' ')}
      // Force each brick onto its own permanently-promoted compositor layer
      // (translateZ(0) + will-change). Without this, mobile WebKit flattens
      // the bricks into the host ScrollContent wrapper's layer; when that
      // wrapper's opacity envelope rises 0→1 (which only happens just before
      // the bricks animate when scrolling UP into the beat), descendant
      // opacity/transform repaints are dropped, stranding bricks mid-fade.
      // A self-owned backing store keeps each brick's opacity/y painting
      // reliably regardless of the parent's opacity transition.
      transformTemplate={(_, generated) =>
        generated === 'none' ? 'translateZ(0)' : `${generated} translateZ(0)`
      }
      style={{
        gridColumn: `${brick.placement.colStart} / span ${brick.placement.colSpan}`,
        gridRow: `${brick.placement.row} / span ${rowSpan}`,
        opacity,
        y,
        willChange: 'transform, opacity',
        // Apex brick is forced square (aspect 1:1) and anchored to the
        // bottom of its 2-row grid cell so it stays visually attached to
        // the row below. Cell width is the binding dimension so the brick
        // ends up width × width, with whatever cell-height surplus sitting
        // as empty space above it (i.e. the top of the pyramid).
        ...(isApex ? { aspectRatio: '1 / 1', alignSelf: 'end' } : {}),
      }}
    >
      {isApex ? (
        <ApexLogoStack />
      ) : (
        <CapabilityContent label={brick.label} Icon={Icon} />
      )}
    </motion.div>
  );
};

const ApexLogoStack: React.FC = () => (
  // Single 1200×1200 composite image with the three platform logos pre-
  // arranged. The apex brick is forced square (see PyramidBrick) and the
  // composite has its own internal whitespace, so no padding is needed
  // here — the image fills the brick edge to edge.
  <img
    src={ASSETS.logoApex3}
    alt="Twitch, Kick, YouTube"
    className="block h-full w-full object-contain"
    // White-rendering filter retained from the per-logo placeholder
    // phase. Remove if the composite is already styled in the teal family.
    style={{ filter: 'brightness(0) invert(1)' }}
    loading="lazy"
    draggable={false}
  />
);

type CapabilityContentProps = {
  label: string;
  Icon?: LucideIcon;
};

const CapabilityContent: React.FC<CapabilityContentProps> = ({
  label,
  Icon,
}) => (
  <div
    className={[
      // Flex-col centered: icon + label render as a single block that's
      // both vertically and horizontally centered inside the brick. When
      // a label wraps to 2 lines, the block is naturally taller, so the
      // icon shifts up to keep the composite centered.
      'flex h-full w-full flex-col items-center justify-center text-center',
      // Padding scales with brick size — kept tighter at lg/xl so the
      // 2-line wrap has breathing room on short laptops (1366×768 etc.).
      'gap-0.5 px-1.5 py-1',
      'sm:gap-1 sm:px-2 sm:py-1.5',
      'md:gap-1.5 md:px-2.5 md:py-1.5',
      'lg:gap-1.5 lg:px-3 lg:py-1.5',
      'min-[1920px]:gap-2 min-[1920px]:py-2',
      'min-[2560px]:gap-2.5 min-[2560px]:py-3',
    ].join(' ')}
  >
    {Icon && (
      <Icon
        className={[
          'shrink-0',
          // Icon size grows with brick size; stays ~25-30% of brick height.
          'h-3 w-3',
          'sm:h-4 sm:w-4',
          'md:h-5 md:w-5',
          'lg:h-6 lg:w-6',
          // Bumped at 1920+ so the icon doesn't read as small relative
          // to the more-spacious brick at that breakpoint.
          'min-[1920px]:h-8 min-[1920px]:w-8',
          'min-[2560px]:h-10 min-[2560px]:w-10',
        ].join(' ')}
      />
    )}
    <span
      className={[
        'block font-semibold leading-tight',
        'text-[0.625rem]',
        'sm:text-xs',
        'md:text-sm',
        'lg:text-sm',
        // Bumped at 1920+ to fill the larger brick's text area; user
        // flagged the previous text-base / text-lg as too small relative
        // to the brick.
        'min-[1920px]:text-xl',
        'min-[2560px]:text-xl',
      ].join(' ')}
      // Reserve a 2-line-tall slot for the label using `em` so it scales
      // with the per-breakpoint font size. With leading-tight (1.25),
      // 2.5em = exactly 2 lines. Result: the icon-plus-label composite has
      // a constant height per breakpoint, so the icon's vertical position
      // doesn't shift between 1-line and 2-line bricks (e.g. "Quick start"
      // sits at the same y as "10-20 minute rounds").
      style={{ minHeight: '2.5em' }}
    >
      {label}
    </span>
  </div>
);

export default BeatSixPyramid;
